/**
 * Builder for the TradingView alert Message body shown on the setup page.
 *
 * Lives outside the page component so the generated JSON can be unit
 * tested: every field here is required by the backend's
 * `TradingViewSignalPayload` (backend/api/schemas.py), and an omission is
 * invisible in the UI — the alert just 422s at fire time, hours later,
 * with nothing on screen to explain it. `timestamp` was missing for
 * exactly that reason, while the self-test button (which builds its own
 * payload server-side) kept reporting success.
 */

/** TradingView placeholders, expanded by TradingView at alert-fire time. */
const ACTION_PLACEHOLDER = "{{strategy.order.action}}";
const PRICE_PLACEHOLDER = "{{close}}";
const TIME_PLACEHOLDER = "{{timenow}}";

export const SECRET_UNREVEALED = "<click 'reveal secret' above>";

export interface AlertTemplateInput {
  secret: string | null;
  strategyId: string;
  symbol: string;
}

/**
 * Returns the pretty-printed JSON to paste into TradingView's Message
 * field.
 *
 * `{{close}}` is emitted UNQUOTED so it becomes a JSON number —
 * TradingView interpolates inside string literals, so a quoted `{{close}}`
 * would arrive as `"1.0850"` and the Decimal field would have to coerce.
 * `{{timenow}}` and `{{strategy.order.action}}` stay quoted: both expand
 * to strings (ISO-8601 UTC, and buy/sell respectively).
 */
export function buildAlertTemplate({
  secret,
  strategyId,
  symbol,
}: AlertTemplateInput): string {
  const obj = {
    secret: secret ?? SECRET_UNREVEALED,
    strategy_id: strategyId,
    symbol,
    action: ACTION_PLACEHOLDER,
    price: PRICE_PLACEHOLDER,
    timestamp: TIME_PLACEHOLDER,
  };
  return JSON.stringify(obj, null, 2).replace(
    `"${PRICE_PLACEHOLDER}"`,
    PRICE_PLACEHOLDER,
  );
}

/**
 * Substitutes the TradingView placeholders with representative values, so
 * tests (and anyone debugging) can check the template is valid JSON of the
 * shape the webhook accepts. Not used by the UI.
 */
export function fillPlaceholders(
  template: string,
  { action = "buy", price = "1.0850", timenow = "2026-09-23T14:30:00Z" } = {},
): string {
  return template
    .replace(ACTION_PLACEHOLDER, action)
    .replace(PRICE_PLACEHOLDER, price)
    .replace(TIME_PLACEHOLDER, timenow);
}
